import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { renderToStaticMarkup } from 'react-dom/server';
import * as moment from 'moment';
import axios from 'axios';
import translate from '../../commonComponents/translate';
import Title from '../../commonComponents/Title';
import ContentContainer from '../../commonComponents/pageLayout/ContentContainer';
import SubHeader from '../../commonComponents/pageLayout/SubHeader';
import FormInput from '../../commonComponents/forms/FormInput';
import DateRange from '../../commonComponents/forms/inputs/DateRange';
import {
  findCurrencySymbol,
  formatPrice,
} from '../../services/utilityFunctions';
import CurrencyProp from '../profile/propTypes/currencyProp';
import PlanProp from './propTypes/planProp';
import PlanHeader from './Components/PlanHeader';
import CountryProp from '../../commonComponents/forms/Address/CountryProp';
import StripeCardProp from '../profile/propTypes/StripeCardProp';
import ReactLoader from '../../commonComponents/loader/ReactLoader';
import ServerSideList from '../../commonComponents/lists/ServerSideList';

function MyPlanPage({ currencies, plan, t, _csrf, cards, countries, tenant }) {
  const [planOptions, setPlanOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const { data } = await axios.get('/api/plan/user/list', {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'License',
        },
      });
      if (data.success) {
        setIsLoading(false);
        data.data.forEach(value => {
          data.data.push({
            text: value.name,
            value: value._id,
          });
        });
        setPlanOptions([
          { text: t('all'), id: '0' },
          ...data.data.map(p => ({ id: p._id, text: p.name })),
        ]);
      }
    })();
  }, []);

  const [query, setQuery] = useState({ userPlan: '' });
  const [filters, setFilters] = useState({ startDate: null, endDate: null });

  const handleDateChange = (startDate, endDate) => {
    setFilters(prevState => ({ ...prevState, startDate, endDate }));
  };

  const planText = status => t(`plan.${status}`);

  const updateQueryStatus = value => {
    setQuery(prev => ({
      ...prev,
      userPlan: value === '0' ? '' : value,
    }));
  };

  const dataTableUrl = '/table/subscription/invoices/list';

  const columns = [
    {
      field: 'date',
      textAlign: 'left',
      title: t('shop.date'),
      width: 100,
      sortable: false,
      template: ({ date }) =>
        renderToStaticMarkup(
          <>{date ? moment(date).format('DD MMM YYYY') : '-'}</>,
        ),
    },
    {
      field: 'paymentDate',
      textAlign: 'left',
      title: t('shop.paymentDate'),
      width: 100,
      sortable: false,
      template: ({ paymentDate }) =>
        renderToStaticMarkup(
          <>{paymentDate ? moment(paymentDate).format('DD MMM YYYY') : '-'}</>,
        ),
    },
    {
      field: 'userPlanName',
      textAlign: 'center',
      title: t('plan.subscription_plan'),
      width: 300,
      sortable: false,
      template: ({ userPlan }) =>
        renderToStaticMarkup(
          <span className="subscription-name">
            {userPlan?.name || `(${t('user.plan.discontinued')})`}
          </span>,
        ),
    },
    {
      field: 'status',
      textAlign: 'left',
      title: t('shop.status'),
      width: 150,
      sortable: false,
      template: item =>
        renderToStaticMarkup(
          <span className={`order-status ${item.status}`}>
            {planText(item.status)}
          </span>,
        ),
    },
    {
      field: 'total',
      textAlign: 'left',
      title: t('plan.priceWithVat'),
      width: 130,
      sortable: false,
      template: ({ total, currency }) =>
        renderToStaticMarkup(
          <>
            {formatPrice(
              total,
              findCurrencySymbol(currency, currencies) || '€',
            )}
          </>,
        ),
    },
    {
      field: 'invoice',
      textAlign: 'left',
      title: t('shop.invoice'),
      width: 120,
      template: item =>
        renderToStaticMarkup(
          item.invoiceNumber ? (
            <a href={item.invoicePdf} target="_blank" rel="noreferrer">
              {item.invoiceNumber}
            </a>
          ) : (
            <>N/A</>
          ),
        ),
    },
  ];
  return (
    <>
      <Title title={t('plan.my_plan')}>
        <ContentContainer>
          <SubHeader title={t('plan.my_plan')} />
          <ReactLoader isLoading={isLoading}>
            <div id="userOrderPage" className="m-content shop">
              <div className="m-portlet__body userOrderPage-container">
                <div className="row col-lg-12 userOrderPage-wrapper">
                  <div className="col-lg-12 userOrderPage-wrapper">
                    <div className="m-portlet">
                      <div className="m-portlet__body m-portlet__body--no-padding">
                        <div className="m-invoice-2">
                          <div className="m-invoice__wrapper">
                            <PlanHeader
                              plan={plan}
                              t={t}
                              _csrf={_csrf}
                              countries={countries}
                              currencies={currencies}
                              paramCards={cards}
                              tenant={tenant}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="m-portlet__body userOrderPage-container">
                <div className="row col-lg-12 userOrderPage-wrapper">
                  <div className="col-lg-12 userOrderPage-wrapper">
                    <div className="m-portlet">
                      <div className="m-portlet__body m-portlet__body--no-padding">
                        <div className="m-invoice-2">
                          <div className="m-invoice__wrapper">
                            <div className="subscriptions-list-wrapper">
                              <div className="row col-lg-12 col-12">
                                <h5 className="orders-list col-12">
                                  {t('plan.invoicesList')}
                                </h5>
                                <FormInput
                                  type="select"
                                  onChange={updateQueryStatus}
                                  id="plan-status"
                                  placeholder={t('plan.status')}
                                  value=""
                                  groupProps={{
                                    className: 'col-lg-3',
                                  }}
                                  inputProps={{
                                    items: planOptions,
                                    allowClear: true,
                                  }}
                                />
                                <div className="col-lg-3">
                                  <DateRange
                                    placeholder={t('shop.date')}
                                    startDate={filters.startDate}
                                    endDate={filters.endDate}
                                    onChange={({ startDate, endDate }) => {
                                      handleDateChange(startDate, endDate);
                                    }}
                                    onClear={() => {
                                      handleDateChange(null, null);
                                    }}
                                  />
                                </div>
                              </div>
                              <ServerSideList
                                columns={columns}
                                id="plan-list"
                                dataSourceURL={dataTableUrl}
                                dataSourceHeaders={{
                                  'Accept-Version': '1.0.0',
                                  'X-Gamification-API': 'License',
                                }}
                                query={{ ...filters, ...query }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ReactLoader>
        </ContentContainer>
      </Title>
    </>
  );
}

MyPlanPage.propTypes = {
  t: PropTypes.func.isRequired,
  plan: PropTypes.shape(PlanProp.propType).isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  cards: PropTypes.arrayOf(PropTypes.shape(StripeCardProp.propType)).isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  _csrf: PropTypes.string.isRequired,
  tenant: PropTypes.shape({}).isRequired,
};

MyPlanPage.defaultProps = {};

export default translate(MyPlanPage);
